import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaChevronDown, FaCheck } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import SportsbookLogo from '../SportsbookLogo';

const Container = styled.div`
  position: relative;
  display: inline-block;
  z-index: 1000;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.theme.inputBg};
  border: 1.5px solid ${props => props.theme.primary};
  border-radius: 0.4rem;
  padding: 0.45rem 0.9rem;
  font-size: 0.95rem;
  color: ${props => props.theme.primaryText};
  cursor: pointer;
  box-shadow: ${props => props.theme.shadow};
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: 140px;
  justify-content: center;
  &:hover, &:focus {
    border-color: ${props => props.theme.primaryHover};
    outline: none;
  }
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.5rem 0.8rem;
    gap: 0.5rem;
    min-width: 120px;
  }
  @media (max-width: 600px) {
    min-width: 0;
    padding: 0 0.65rem;
    gap: 0.25rem;
    height: 2.5rem;
    min-height: 2.5rem;
  }
  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 0 0.55rem;
    height: 2.5rem;
    min-height: 2.5rem;
  }
`;

const SportsbookLabel = styled.span`
  @media (max-width: 600px) {
    display: none;
  }
`;

const OddsIcon = styled.span`
  display: none;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.primary};
  @media (max-width: 600px) {
    display: inline;
  }
`;

/** Logo image for sportsbook (mobile condensed view). Use SVG or PNG in public/sportsbook-logos/{key}.svg */
const SportsbookLogoImg = styled.img`
  display: none;
  height: 1.25em;
  width: auto;
  max-height: 1.5rem;
  object-fit: contain;
  vertical-align: middle;
  @media (max-width: 600px) {
    display: ${props => (props.$show ? 'inline-block' : 'none')};
    max-height: 1.35rem;
  }
`;

const ChevronIcon = styled(FaChevronDown)`
  color: ${props => props.theme.mutedText};
  font-size: 1em;
  transition: transform 0.2s;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  @media (max-width: 768px) { font-size: 0.9em; }
  @media (max-width: 480px) { font-size: 0.8em; }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  /* width follows content so the menu is not wider than needed; cap by viewport on small screens */
  width: max-content;
  max-width: calc(100vw - 1rem);
  background: ${props => props.theme.cardBg};
  border: 1px solid ${props => props.theme.border};
  border-radius: 0.25rem;
  box-shadow: ${props => props.theme.shadowHover};
  z-index: 1000;
  margin-top: 0.25rem;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
  @media (min-width: 769px) {
    max-width: min(240px, calc(100vw - 1.5rem));
  }
`;

const OptionRow = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  min-width: 0;
  padding: 0.45rem 0.55rem;
  cursor: pointer;
  font-size: 0.8125rem;
  text-align: left;
  border: none;
  background: ${props => props.$selected ? props.theme.primary : 'transparent'};
  color: ${props => props.$selected ? (props.theme.inverseText || '#fff') : props.theme.primaryText};
  transition: background-color 0.2s, color 0.2s;
  white-space: nowrap;
  &:hover {
    background-color: ${props => props.$selected ? props.theme.primary : props.theme.tertiaryBg};
  }
  &:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.border};
  }
  @media (max-width: 480px) {
    font-size: 0.8125rem;
    padding: 0.4rem 0.5rem;
    gap: 0.35rem;
  }
`;

/** Fixed slot so check + logo + label columns line up across rows */
const OptionLogoCell = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.35rem;
  min-height: 1.2rem;
`;

/** “Best odds” row uses $ like the trigger button */
const BestOddsMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1;
  color: ${(props) => (props.$selected ? (props.theme.inverseText || '#fff') : props.theme.primary)};
`;

const OptionName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CheckIcon = styled(FaCheck)`
  font-size: 0.75em;
  flex-shrink: 0;
  opacity: ${props => props.$selected ? 1 : 0};
`;

/**
 * Sportsbook dropdown that matches Filters dropdown expanded menu styling.
 * Uses a custom list (not native select) so option text size is readable on mobile.
 */
const LOGO_BASE = `${process.env.PUBLIC_URL || ''}/sportsbook-logos`;

function SportsbookDropdown({ options, value, onChange, ariaLabel = 'Sportsbook for odds' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [tryPng, setTryPng] = useState(false);
  const { theme } = useTheme();
  const list = options?.length ? options : [{ key: 'best', name: 'Best Odds' }];
  const selectedName = list.find((b) => b.key === value)?.name ?? 'Best Odds';
  const isBestOdds = value === 'best';

  // Reset logo state when sportsbook changes
  useEffect(() => {
    setLogoError(false);
    setTryPng(false);
  }, [value]);

  const handleLogoError = () => {
    if (!tryPng) setTryPng(true);
    else setLogoError(true);
  };
  const logoSrc = tryPng ? `${LOGO_BASE}/${value}.png` : `${LOGO_BASE}/${value}.svg`;

  const handleClickOutside = (e) => {
    if (!e.target.closest('.sportsbook-dropdown')) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <Container className="sportsbook-dropdown">
      <Button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        theme={theme}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {isBestOdds ? (
          <OddsIcon theme={theme} aria-hidden>$</OddsIcon>
        ) : logoError ? (
          <OddsIcon theme={theme} aria-hidden>{selectedName.charAt(0) || '?'}</OddsIcon>
        ) : (
          <SportsbookLogoImg
            $show
            src={logoSrc}
            alt=""
            aria-hidden
            onError={handleLogoError}
          />
        )}
        <SportsbookLabel>{selectedName}</SportsbookLabel>
        <ChevronIcon $isOpen={isOpen} theme={theme} />
      </Button>
      <DropdownContent $isOpen={isOpen} theme={theme} role="listbox">
        {list.map((b) => (
          <OptionRow
            key={b.key}
            type="button"
            role="option"
            aria-selected={value === b.key}
            $selected={value === b.key}
            theme={theme}
            onClick={() => {
              onChange(b.key);
              setIsOpen(false);
            }}
          >
            <CheckIcon $selected={value === b.key} theme={theme} />
            <OptionLogoCell aria-hidden>
              {b.key === 'best' ? (
                <BestOddsMark theme={theme} $selected={value === b.key}>$</BestOddsMark>
              ) : (
                <SportsbookLogo provider={b.key} size="1.05rem" ariaLabel="" />
              )}
            </OptionLogoCell>
            <OptionName>{b.name}</OptionName>
          </OptionRow>
        ))}
      </DropdownContent>
    </Container>
  );
}

export default SportsbookDropdown;
